'use client';

import { SDKResponse } from '@commercetools/frontend-sdk';
import jwt, { JwtPayload } from 'jsonwebtoken';
import Cookies from 'universal-cookie';
import { WithSDKError } from 'shared/types/shared';
import { authenticateUser } from 'app/actions';
import { rootStore, setAccountId, setIsSessionInvalid } from 'store';

export const loginRedirectUrl = () => {
  const edxLoginEndpoint = process.env.NEXT_PUBLIC_EDX_LOGIN_ENDPOINT!;
  const url = encodeURIComponent(window.location.href);
  return `${edxLoginEndpoint}?next=${url}`;
};

const getJwtPayload = () => {
  const jwtPayloadCookieName =
    process.env.NEXT_PUBLIC_EDX_JWT_PAYLOAD_COOKIE_NAME!;
  const jwtPayloadCookieValue = new Cookies().get<string>(jwtPayloadCookieName);

  if (jwtPayloadCookieValue) {
    try {
      return jwt.decode(`${jwtPayloadCookieValue}.`) as JwtPayload;
    } catch (err) {
      return null;
    }
  }
  return null;
};

export const isCookieValid = (expiryThreshold = 0) => {
  const jwtPayload = getJwtPayload();
  if (jwtPayload) {
    return jwtPayload.exp! > Date.now() / 1000 + expiryThreshold;
  }
  return false;
};

export const withCookieValidation = async <T extends WithSDKError>(
  fn: () => Promise<SDKResponse<T>>,
): Promise<SDKResponse<T>> => {
  const locale = 'en';
  if (!isCookieValid(30 * 60)) {
    const loginRefreshResponse = await fetch(
      process.env.NEXT_PUBLIC_EDX_LOGIN_REFRESH_ENDPOINT!,
      {
        method: 'POST',
        credentials: 'include',
      },
    );

    if (!loginRefreshResponse.ok) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      rootStore.dispatch(setIsSessionInvalid(true));
      return {
        data: {
          isError: true,
          error: {
            message: 'failed to refresh user session',
            code: 'authentication_error',
            status: 401,
          },
        },
      } as SDKResponse<T>;
    }

    const accountResult = await authenticateUser(locale);

    if (
      !accountResult ||
      !!accountResult.isError ||
      !!accountResult.data.isError ||
      !accountResult.data.loggedIn
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      rootStore.dispatch(setIsSessionInvalid(true));
      return {
        data: {
          isError: true,
          error: {
            message: 'SSO error',
            code: 'authentication_error',
            status: 401,
          },
        },
      } as SDKResponse<T>;
    }
    if (accountResult.data?.account?.accountId) {
      rootStore.dispatch(setAccountId(accountResult.data.account.accountId));
    }
  }

  const response = await fn();

  if (
    !response.isError &&
    response.data.isError &&
    (response.data.error.code === 'account_authentication_error' ||
      response.data.error.message.includes('TokenExpiredError'))
  ) {
    rootStore.dispatch(setIsSessionInvalid(true));
  }

  return response;
};
